import { useFragment } from '@apollo/client/react';
import { useCallback, useEffect, useRef, type FunctionComponent } from 'react';

import {
  type FeedbackResult,
  useFeedbackCollector,
} from '@trello/ai-labs/feedback-collector';
import { Analytics } from '@trello/atlassian-analytics';
import { getMemberIdWithFallback } from '@trello/authentication';
import { fireConfetti } from '@trello/confetti';
import { useBoardId } from '@trello/id-context';
import { useSharedStateSelector } from '@trello/shared-state';
import { TrelloStorage } from '@trello/storage';

import { aiBoardPromptLoadingGradientState } from 'app/src/components/AiBoardBuilder/AiBoardBuilderCommandCenter/aiBoardPromptLoadingGradientState';
import { getAiBoardBuilderFeedbackAttributes } from 'app/src/components/AiBoardBuilder/getAiBoardBuilderFeedbackAttributes';
import { trackAiBoardBuilderFeedbackSubmitted } from 'app/src/components/AiBoardBuilder/trackAiBoardBuilderFeedbackSubmitted';
import { useIsAibbM2Enabled } from 'app/src/components/AiBoardBuilder/useIsAibbM2Enabled';
import { AiBoardLoadingGradientFragmentDoc } from './AiBoardLoadingGradientFragment.generated';
import { AiLoadingGradient } from './AiLoadingGradient';

interface AIBoardLoadingGradientProps {
  className?: string;
}

/**
 * Renders a gradient overlay while an AI board is being generated.
 * This is a standalone component (not a wrapper) that should be placed
 * within BoardView as a sibling to the board content.
 *
 * Shows the loading gradient for any board with creationMethod === 'ai'
 * that has creationMethodLoadingStartedAt set (i.e. still streaming).
 * Fires confetti when loading completes.
 */
export const AiBoardLoadingGradient: FunctionComponent<
  AIBoardLoadingGradientProps
> = ({ className }) => {
  const boardId = useBoardId();
  const isEditBoardPromptLoading = useSharedStateSelector(
    aiBoardPromptLoadingGradientState,
    (state) => state,
  );
  const { isAibbM2Enabled } = useIsAibbM2Enabled();

  const { data: fragmentData, complete } = useFragment({
    fragment: AiBoardLoadingGradientFragmentDoc,
    fragmentName: 'AiBoardLoadingGradient',
    from: {
      id: boardId,
      __typename: 'Board',
    },
  });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const data = complete ? fragmentData : undefined;

  const onFeedbackGiven = useCallback(
    (feedbackResult: FeedbackResult, traceId?: string) =>
      trackAiBoardBuilderFeedbackSubmitted(boardId, feedbackResult, traceId),
    [boardId],
  );
  const { showFeedbackCollector } = useFeedbackCollector({
    featureName: 'aiBoardBuilder',
    onFeedbackGiven,
  });

  // Use a ref to keep track of the number of cards because we don't want to
  // refire confetti every time the number of cards changes on the board thereafter
  // but we do want an up-to-date count when the board finishes loading
  const numCardsCountRef = useRef<number>(0);
  useEffect(() => {
    // data?.cards.length can be undefined while the fragment is partial;
    // default to 0 to keep the ref strictly typed as number.
    numCardsCountRef.current = data?.cards.length ?? 0;
  }, [data]);

  const isAiBoard = data?.creationMethod === 'ai';
  const isAiLoading = isAiBoard && !!data?.creationMethodLoadingStartedAt;
  const shouldShowGradient =
    isAiLoading || (isAibbM2Enabled && isEditBoardPromptLoading);

  // Track if AI loading has ever been active to detect completion
  const wasAiLoadingRef = useRef(false);

  // Fire confetti when AI loading completes
  useEffect(() => {
    if (wasAiLoadingRef.current && !isAiLoading && isAiBoard) {
      fireConfetti({
        x: 0.5,
        y: 0.3,
        spread: 180,
        particleCount: 200,
      });
      const memberId = getMemberIdWithFallback();
      if (!isAibbM2Enabled) {
        showFeedbackCollector({
          attributes: getAiBoardBuilderFeedbackAttributes(boardId),
          flagTreatment: true,
          traceId: TrelloStorage.get(`aiBoardBuilderSpanId-${memberId}`),
        });
        // The collector above has the span id now, so the creation journey is
        // over. On the M2 path the command center shows the collector instead,
        // and clears the span itself once it has read it — clearing here would
        // leave its feedback with no journey to join.
        TrelloStorage.unset(`aiBoardBuilderSpanId-${memberId}`);
      }
      const timeStarted = parseInt(
        TrelloStorage.get(`aiBoardBuilderCreationStartedAt-${memberId}`) || '0',
        10,
      );
      const timeSpent = Date.now() - timeStarted;
      Analytics.sendOperationalEvent({
        action: 'finished',
        actionSubject: 'aiBoardBuilder',
        source: 'boardScreen',
        attributes: {
          timeSpent,
          numCards: numCardsCountRef.current,
        },
        containers: {
          board: { id: boardId },
        },
      });
      TrelloStorage.unset(`aiBoardBuilderCreationStartedAt-${memberId}`);
    }
    if (isAiLoading) {
      wasAiLoadingRef.current = true;
    }
  }, [isAiLoading, isAiBoard, boardId, showFeedbackCollector, isAibbM2Enabled]);

  if (!shouldShowGradient) {
    return null;
  }

  return (
    <AiLoadingGradient
      className={className}
      testId="ai-board-loading-gradient"
    />
  );
};
